import React from "react";

interface OcPasswordStrengthMeterProps {
  password: string;
  /**
   * Neutral mode (the directory's own password policy applies): no
   * Weak/Strong verdict or red/green, since only the directory decides —
   * gray bars that fill with length and variety, plus a character count.
   */
  neutral?: boolean;
}

function ocComputeScore(password: string): number {
  if (!password) return 0;

  let score = 0;
  const length = password.length;

  // Length contribution
  if (length >= 8) score += 1;
  if (length >= 12) score += 1;

  // Character class diversity
  let classes = 0;
  if (/[a-z]/.test(password)) classes += 1;
  if (/[A-Z]/.test(password)) classes += 1;
  if (/[0-9]/.test(password)) classes += 1;
  if (/[^A-Za-z0-9]/.test(password)) classes += 1;

  if (classes >= 2) score += 1;
  if (classes >= 4) score += 1;

  // Clamp to 0-4
  if (score > 4) score = 4;
  if (length === 0) score = 0;

  return score;
}

const OC_LABELS = ["", "Weak", "Fair", "Good", "Strong"];

function ocSegmentColor(score: number, index: number): string {
  if (index >= score) return "#e2e8f0"; // slate-200 (empty)
  if (score <= 1) return "#dc2626"; // red-600
  if (score === 2) return "#f59e0b"; // amber-500
  if (score === 3) return "#84cc16"; // lime-500
  return "#16a34a"; // green-600
}

function ocLabelColor(score: number): string {
  if (score <= 1) return "#dc2626";
  if (score === 2) return "#b45309"; // amber-700
  if (score === 3) return "#4d7c0f"; // lime-700
  return "#15803d"; // green-700
}

const OcPasswordStrengthMeter: React.FC<OcPasswordStrengthMeterProps> = ({
  password,
  neutral = false,
}) => {
  const score = ocComputeScore(password);
  const label = score === 0 ? "" : OC_LABELS[score];

  if (neutral) {
    const count = password.length;
    return (
      <div className="oc-password-strength-meter mt-2">
        <div className="flex gap-1.5" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-1.5 flex-1 rounded-full transition-colors duration-150"
              style={{ backgroundColor: i < score ? "#64748b" : "#e2e8f0" }}
            />
          ))}
        </div>
        <div className="mt-1.5 text-xs min-h-[1rem] text-slate-500" aria-live="polite">
          {count > 0 ? `${count} character${count === 1 ? "" : "s"}` : " "}
        </div>
      </div>
    );
  }

  return (
    <div className="oc-password-strength-meter mt-2">
      <div className="flex gap-1.5" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full transition-colors duration-150"
            style={{ backgroundColor: ocSegmentColor(score, i) }}
          />
        ))}
      </div>
      <div
        className="mt-1.5 text-xs font-medium min-h-[1rem]"
        style={{ color: label ? ocLabelColor(score) : "transparent" }}
        aria-live="polite"
      >
        {label || " "}
      </div>
    </div>
  );
};

export { OcPasswordStrengthMeter };
export default OcPasswordStrengthMeter;
